import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { auth } from '../../services/firebase';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import AdminSidebar from './components/AdminSidebar';
import AdminHeader from './components/AdminHeader';
import DashboardOverview from './components/DashboardOverview';
import ServicesManager from './components/ServicesManager';
import ProjectsManager from './components/ProjectsManager';
import TestimonialsManager from './components/TestimonialsManager';
import BlogManager from './components/BlogManager';
import TeamManager from './components/TeamManager';
import CalculatorsManager from './components/CalculatorsManager';
import SettingsManager from './components/SettingsManager';
import HomepageManager from './components/HomepageManager';

const AdminPanel = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const mainRef = React.useRef(null);

  // Scroll to top when section changes
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
  }, [activeSection]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setIsSidebarOpen(false); // Close sidebar on mobile
  };

  // Firebase Auth Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAuthenticated(true);
        setAdminUser({
          name: user.displayName || user.email,
          email: user.email,
          role: 'Admin'
        });
      } else {
        setIsAuthenticated(false);
        setAdminUser(null);
        // Reset states on logout
        setEmail('');
        setPassword('');
        setActiveSection('dashboard');
        setShowLoginPassword(false);
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Login error:", error);
      setLoginError('Invalid email or password. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardOverview onSectionChange={handleSectionChange} />;
      case 'services':
        return <ServicesManager />;
      case 'projects':
        return <ProjectsManager />;
      case 'testimonials':
        return <TestimonialsManager />;
      case 'blog':
        return <BlogManager />;
      case 'team':
        return <TeamManager />;
      case 'calculators':
        return <CalculatorsManager />;
      case 'homepage':
        return <HomepageManager />;
      case 'settings':
        return <SettingsManager />;
      default:
        return <DashboardOverview onSectionChange={handleSectionChange} />;
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading admin panel...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 bg-primary rounded-xl flex items-center justify-center mb-4">
              <span className="text-primary-foreground text-2xl">⚡</span>
            </div>
            <h2 className="text-3xl font-headline font-bold text-foreground">Admin Login</h2>
            <p className="mt-2 text-muted-foreground">Access Nirmitee Associates CMS</p>
          </div>
          <div className="bg-card p-8 rounded-xl shadow-construction border border-border">
            <form className="space-y-6" onSubmit={handleLogin} autoComplete="off">
              {/* Fake inputs to trick Chrome's aggressive password manager */}
              <input style={{ display: 'none' }} type="text" name="fakeusernameremembered" />
              <input style={{ display: 'none' }} type="password" name="fakepasswordremembered" />
              {loginError && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                  <span className="text-lg">⚠️</span> {loginError}
                </div>
              )}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  placeholder="nirmitee.associates2919@gmail.com"
                  autoComplete="new-password"
                  name="admin_login_email"
                  required
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all pr-10"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    name="admin_login_password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
              >
                Sign In
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background font-sans text-foreground flex flex-col overflow-hidden">
      <AdminHeader
        adminUser={adminUser}
        onLogout={handleLogout}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        onSectionChange={handleSectionChange}
      />

      <div className="flex flex-1 pt-16 overflow-hidden">
        <AdminSidebar
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          isOpen={isSidebarOpen}
          setIsOpen={setIsSidebarOpen}
        />

        <main ref={mainRef} className="flex-1 overflow-y-auto p-4 md:p-6 w-full">
          <div className="bg-card rounded-xl shadow-sm border border-border p-4 md:p-6 min-h-full animate-fade-in relative z-0">
            {renderActiveSection()}
          </div>
        </main>

        {/* Mobile Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-50 lg:hidden backdrop-blur-sm transition-opacity"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPanel;